import { inject, injectable } from "inversify";
import { AjaxService, LoaderService, MessageService, ScriptService, ValidationService } from ".";
import { IpfModule } from "..";
import { MessageType } from "../enums.generated";
import { callFunction } from "../functions/call-function";
import { logInfo } from "../functions/client-log";
import { getProperty } from "../functions/get-property";
import { TYPES } from "../ioc/types";
import { AjaxRequestOptions, HttpMethods } from "../models/http";
import { ServiceBase } from "./ServiceBase";

/**
 * AJAx service, sends AJAX requests and configures forms to perform AJAX postbacks on submit.
 */
@injectable()
export class JQueryAjaxService extends ServiceBase implements AjaxService {

	/**
	 * Initialises a new instance of the IpfPageService class.
	 * @param ipfModule IPF module, containing contextual information and functions.
	 * @param loaderService Loader service, used to show loading elements.
	 * @param messageService Message service, used to display messages.
	 * @param scriptService Script service, used to execute scripts.
	 * @param validationService Validation service, used to apply validation functionality to forms.
	 */
	public constructor(
		@inject(TYPES.IpfModule) private ipfModule: IpfModule,
		@inject(TYPES.LoaderService) private loaderService: LoaderService,
		@inject(TYPES.MessageService) private messageService: MessageService,
		@inject(TYPES.ScriptService) private scriptService: ScriptService,
		@inject(TYPES.ValidationService) private validationService: ValidationService
	) {
		super();
	}

	/**
	 * @inheritdoc
	 */
    public async request<TResponseType>(options: AjaxRequestOptions<TResponseType>): Promise<TResponseType | null> {

        const settings: JQuery.AjaxSettings = {};
        settings.url = options.url;
		settings.method = options.method?.toUpperCase() || HttpMethods.Get;

        if (options.data) {

            settings.contentType = "application/json; charset=utf-8";
            settings.data = JSON.stringify(options.data, (key, value) => {

                let returnValue: any;

                if (key.startsWith("_")) {

                    returnValue = undefined;
                }
                else {

                    returnValue = value;
                }

                return returnValue;
			});
        }
        else {

            settings.contentType = false;
		}

		const headers: JQuery.PlainObject<string> = {};

		if (options.authToken) {

			headers["Authorization"] = `Bearer ${options.authToken}`;
		}

		// adds the antiforgery token to the RequestVerificationToken header
        if (settings.method !== "GET") {

            const token = $('input:hidden[name="__RequestVerificationToken"]').val();

            if (token) {

				headers["RequestVerificationToken"] = token as string;
            }
        }

		settings.headers = headers;
        settings.dataType = "json";

        return new Promise<TResponseType | null>((resolve, reject) => {

			settings.success = (data, status, response) => {

                if (status === "success") {

					resolve(data);

					// handle cases where a redirect is required
					const location = response.getResponseHeader("Location") as string;
					if (location) {
						window.location.href = location;
					}

					if (options.success) {

						options.success(data);
					}

					return data;
                }
                else {

                    reject({
						data,
                        status
                    });
                }
            };

            settings.error = (response, status, message) => {

				this.handleAjaxError(response, status, message);

                reject({
                    response,
                    status,
                    message
                });
			};

			settings.complete = () => {

				this.ipfModule.refreshSessionTimeout();
			};

            $.ajax(settings);
        });
	}

	/**
	 * @inheritdoc
	 */
	public unobstrusive(container: HTMLElement): void {

		logInfo("ajax-service-unobstrusive", "AjaxService");

		// find all <form> elements with a data-ajax attribute
		container.querySelectorAll("form[data-ajax]").forEach(form => {

			logInfo("ajax-service-unobstrusive-form", "AjaxService");

			// setup the AJAX form
			this.setupAjaxForm(form as HTMLFormElement);
		});
	}

	private setupAjaxForm(form: HTMLFormElement): void {

		logInfo("ajax-service-setupAjaxForm", "AjaxService");

		// add an on submit event listener
		form.addEventListener("submit", event => {

			logInfo("ajax-service-onsubmit", "AjaxService");

			let disableAjax: boolean | undefined;

			try {

				const eventObj = event as any;
				let submitter: Element | null = eventObj.submitter as Element;

				// check for a submitter element on the event object
				if (!submitter) {

					// safari workaround for SubmitEvent.submitter and document.activeElement=<body> browser bugs:
					// when document.activeElement is null or is <body>, assign ipfModule.ensuredActiveElement instead.
					let ensuredActiveElement = this.ipfModule.ensuredActiveElement ?? document.activeElement;

					// if there is no active element then fallback to the event target <form>
					if (!ensuredActiveElement) {
						ensuredActiveElement = event.target as Element;
					}

					submitter = ensuredActiveElement;

					logInfo("ajax-service-submitter: " + submitter.getAttribute("id") ?? submitter.getAttribute("formaction"), "AjaxService");
				}

				// validate the form, if not valid then don't submit the ajax form
				if ((disableAjax = submitter.getAttribute("data-disable-ajax") === "true") ||
					(submitter.getAttribute("formnovalidate") == null &&
						!(submitter.classList.contains("form-no-validate") || submitter.classList.contains("MuiDataGrid-cell")) &&
						this.validationService.isValidForm(form) === false)) {

					logInfo("ajax-service-not-valid", "AjaxService");

					return;
				}

                let loadingTimeout: NodeJS.Timeout;
                let ajaxContainer = form.parentElement;
                let ajaxTimeout: number = parseInt(form.getAttribute("data-ajax-timeout") || "0");

				// show a loader element over the form
                if (ajaxContainer && form.parentElement) {

                    if (!ajaxContainer.classList.contains("ajax-container")) {

                        ajaxContainer = document.createElement("div");
                        ajaxContainer.className = "ajax-container";

                        form.parentElement.insertBefore(ajaxContainer, form);

                        ajaxContainer.appendChild(form);
                    }

                    loadingTimeout = global.setTimeout(() => {
                        if (ajaxContainer) {
                            form.classList.add("form-loading");
                            this.loaderService.render(ajaxContainer);
                        }
                    }, ajaxTimeout > 0 ? 0 : 250);
				}

				// call any AJAX before request handler
				const ajaxBefore = form.getAttribute("data-ajax-before");
				if (ajaxBefore) {

					callFunction(ajaxBefore);
				}

				// build $.ajax options
				let url: string | null = submitter?.getAttribute("formaction");
                const settings: JQuery.AjaxSettings = {};
				settings.url = url ?? form.action;
				settings.method = form.method?.toUpperCase() || HttpMethods.Post;
                settings.data = new FormData(form);
                settings.processData = false;
				settings.contentType = false;
				settings.error = (response: JQuery.jqXHR, status: string, message: string) => {

					this.handleAjaxError(response, status, message);

					if (ajaxContainer) {

						this.loaderService.remove(ajaxContainer);
						form.classList.remove("form-loading");
					}
				};

				// successful response handler function
				settings.success = (data, status, response) => {

					// handle cases where a redirect is required following an ajax form post
					const location = response.getResponseHeader("Location") as string;
					if (location) {
						window.location.href = location;
					}
				};

				// setTimeout allows any timeout delay for the request
				setTimeout(() => {

					this.messageService.clearMessages();

					logInfo("ajax-service-submit", "AjaxService");

					$.ajax(settings)
                        .then((response: string, status: JQuery.Ajax.SuccessTextStatus) => {

							// if successful return the expected response HTML
							if (status === "success") {

                                return response;
                            }
                            else {

                                return null;
                            }
                        })
                        .then(html => {

							if (ajaxContainer) {

								if (html) {

									// update the AJAX containers HTML
									$(ajaxContainer).html(html);

									// find the replacement form and set it up again
									const newForm = ajaxContainer.querySelector("form") as HTMLFormElement;
									this.setupAjaxForm(newForm);
									this.validationService.reapplyUnobtrusiveValidator(newForm);
									this.scriptService.run();
								}
								else {

									form.classList.remove("form-loading");
								}

								// call any provided AJAX complete handler
								const ajaxComplete = form.getAttribute("data-ajax-complete");
								if (ajaxComplete) {

									callFunction(ajaxComplete, ajaxContainer);
								}
                            }
						})
						.catch(() => {

							// handle request errors

							const ajaxError = form.getAttribute("data-ajax-error");

							if (ajaxError) {

								const onError = getProperty(window, ajaxError);

								// call on error handler if provided
								if (typeof onError === "function") {

									onError();
								}
							}
						})
                        .always(() => {

							// AJAX complete handler, always called whether request succeeeds or fails
							if (ajaxContainer) {

								// always remove the loader
								this.loaderService.remove(ajaxContainer);
							}

							// clear loader display timeout, in case request completed quicker than the timeout
							clearTimeout(loadingTimeout);

							// refresh session timer as the user did something
							this.ipfModule.refreshSessionTimeout();
                        });
                }, ajaxTimeout);
            }
            finally {

				// always clear the ensured active element
				if (this.ipfModule.ensuredActiveElement) {

					this.ipfModule.ensuredActiveElement = null;
				}

				if (!disableAjax) {

					event.preventDefault();
					return false;
				}
            }
        });
	}

	private handleAjaxError(response: JQuery.jqXHR, status: string, message: string) {

		switch (response.status) {
			case 401:
				// if unauthorised reload the page to trigger a redirect
				window.location.reload();
				break;
			default:
				// handle 500 errors and bad request, typically a failure of modelstate/antiforgery validation
				this.messageService.showMessage({
					title: "Page Error",
					text: this.ipfModule.context.content["GeneralApiErrorMessage"] ?? "",
					type: MessageType.Error
				});
				break;
		}
	}
}